import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Alert, CircularProgress, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Stack, InputAdornment,
    Pagination, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    TableChart as TableChartIcon,
    AttachFile as AttachFileIcon,
    Delete as DeleteIcon,
    DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { API_URL, deleteSupply, deleteSuppliesCompetencia, addSupplyItem, updateSupplyItem, deleteSupplyItem, deleteSuppliesCompetencia as deleteSuppliesCompetenciaAPI } from '../api/api';
import { useCompetence } from '../contexts/CompetenceContext';

const SupplyTablesPage = () => {
    const { globalCompetence, availableMonths, refreshMonths } = useCompetence();

    const [competencias, setCompetencias] = useState([]);
    const [selectedCompetencia, setSelectedCompetencia] = useState('');
    // uploadCompetencia removed, using selectedCompetencia instead
    // uploadCompetencia removed, using selectedCompetencia instead
    const [file, setFile] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, competencia: null });
    const [deleting, setDeleting] = useState(false);

    // CRUD State
    const [openModal, setOpenModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({ codigo_insumo: '', codigo_tuss: '', descricao: '', custo_medio: '' });
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);

    // CRUD Handlers
    const handleOpenAdd = () => {
        setEditingItem(null);
        setFormData({ codigo_insumo: '', codigo_tuss: '', descricao: '', custo_medio: '' });
        setOpenModal(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setFormData({
            codigo_insumo: item.codigo_insumo,
            codigo_tuss: item.codigo_tuss || '',
            descricao: item.descricao || '',
            custo_medio: item.custo_medio || ''
        });
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingItem(null);
    };

    const handleSave = async () => {
        try {
            if (!editingItem && !selectedCompetencia) {
                setMessage({ type: 'error', text: 'Selecione uma competência antes de adicionar um item.' });
                return;
            }
            if (!formData.codigo_insumo || formData.codigo_insumo.trim() === '') {
                setMessage({ type: 'error', text: 'O Código do Insumo é obrigatório.' });
                return;
            }

            const payload = {
                ...formData,
                custo_medio: formData.custo_medio && !isNaN(parseFloat(formData.custo_medio)) ? parseFloat(formData.custo_medio) : null
            };

            if (editingItem) {
                await updateSupplyItem(editingItem.id, payload);
                setMessage({ type: 'success', text: 'Item atualizado com sucesso!' });
            } else {
                await addSupplyItem({ ...payload, competencia: selectedCompetencia });
                setMessage({ type: 'success', text: 'Item adicionado com sucesso!' });
            }
            handleCloseModal();
            fetchItems();
        } catch (error) {
            console.error('Erro ao salvar item:', error);
            const errorMsg = error.response?.data?.mensagem || 'Erro ao salvar item. Verifique os dados.';
            setMessage({ type: 'error', text: errorMsg });
        }
    };

    const handleDeleteCRUD = async () => {
        if (!deleteConfirmation) return;
        try {
            await deleteSupplyItem(deleteConfirmation);
            setMessage({ type: 'success', text: 'Item excluído com sucesso!' });
            setDeleteConfirmation(null);
            fetchItems();
        } catch (error) {
            console.error('Erro ao excluir item:', error);
            setMessage({ type: 'error', text: 'Erro ao excluir item.' });
        }
    };

    const handleDeleteAllSupplies = async () => {
        if (!selectedCompetencia) {
            setMessage({ type: 'error', text: 'Selecione uma competência primeiro.' });
            return;
        }

        if (!window.confirm(`Tem certeza que deseja excluir TODOS os ${total} registros de Insumos da competência ${selectedCompetencia}? Esta ação não pode ser desfeita.`)) {
            return;
        }

        try {
            await deleteSuppliesCompetenciaAPI(selectedCompetencia);
            setMessage({ type: 'success', text: 'Todos os registros de Insumos foram excluídos com sucesso!' });
            fetchItems();
        } catch (error) {
            console.error('Erro ao excluir insumos:', error);
            setMessage({ type: 'error', text: 'Erro ao excluir registros de insumos.' });
        }
    };

    useEffect(() => {
        if (availableMonths.length > 0) {
            setCompetencias(availableMonths);
        }
    }, [availableMonths]);

    // Set initial value from global competence
    useEffect(() => {
        if (globalCompetence && competencias.length > 0 && !selectedCompetencia) {
            if (competencias.includes(globalCompetence)) {
                setSelectedCompetencia(globalCompetence);
            }
        }
    }, [globalCompetence, competencias, selectedCompetencia]);

    useEffect(() => {
        if (selectedCompetencia) {
            fetchItems();
        } else {
            setItems([]);
            setTotal(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompetencia, page, search]);

    // fetchCompetencias removed as we use availableMonths from context

    const fetchItems = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({
                competencia: selectedCompetencia,
                page,
                search,
                per_page: 50
            });
            const res = await fetch(`${API_URL}/supplies?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();
            setItems(data.items);
            setTotal(data.total);
        } catch (error) {
            console.error("Erro ao buscar itens:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteItem = async () => {
        setDeleting(true);
        try {
            await deleteSupply(deleteDialog.id);
            setMessage({ type: 'success', text: 'Insumo excluído com sucesso!' });
            fetchItems();
            setDeleteDialog({ open: false, type: null, id: null, competencia: null });
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao excluir insumo: ${error.response?.data?.mensagem || error.message}` });
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCompetencia = async () => {
        setDeleting(true);
        try {
            const response = await deleteSuppliesCompetencia(deleteDialog.competencia);
            setMessage({ type: 'success', text: response.data.mensagem || 'Insumos excluídos com sucesso!' });
            fetchItems();
            refreshMonths();
            setDeleteDialog({ open: false, type: null, id: null, competencia: null });
        } catch (error) {
            setMessage({ type: 'error', text: `Erro ao excluir insumos: ${error.response?.data?.mensagem || error.message}` });
        } finally {
            setDeleting(false);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        e.preventDefault();
        if (!file || !selectedCompetencia) {
            setMessage({ type: 'error', text: 'Selecione um arquivo e uma competência.' });
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('competencia', selectedCompetencia);

        setUploading(true);
        setMessage(null);

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/supplies/upload`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();

            if (res.ok) {
                setMessage({ type: 'success', text: data.message });
                setFile(null);
                // Reset file input
                const fileInput = document.getElementById('fileInput');
                if (fileInput) fileInput.value = '';

                refreshMonths();
                fetchItems();
            } else {
                setMessage({ type: 'error', text: data.error || 'Erro no upload.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Erro de conexão.' });
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <TableChartIcon sx={{ mr: 2, color: 'primary.main' }} /> Tabelas de Insumo
            </Typography>

            {/* Upload Section */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <CloudUploadIcon sx={{ mr: 1, color: 'primary.main' }} /> Importar Tabela
                </Typography>
                <form onSubmit={handleUpload}>
                    <Grid container spacing={3} alignItems="flex-end">
                        <Grid item xs={12} md={9}>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                startIcon={<AttachFileIcon />}
                                sx={{ height: 56, justifyContent: 'flex-start' }}
                            >
                                {file ? file.name : "Selecionar Arquivo (.xlsx, .txt, .csv)"}
                                <input
                                    id="fileInput"
                                    type="file"
                                    hidden
                                    accept=".xlsx,.txt,.csv"
                                    onChange={handleFileChange}
                                    required
                                />
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={uploading}
                                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                sx={{ height: 56 }}
                            >
                                {uploading ? 'Enviando...' : 'Importar'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
                {message && (
                    <Alert severity={message.type} sx={{ mt: 3 }}>
                        {message.text}
                    </Alert>
                )}
            </Paper>

            {/* Filter & List Section */}
            <Paper sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Competência</InputLabel>
                            <Select
                                value={selectedCompetencia}
                                label="Competência"
                                onChange={(e) => {
                                    setSelectedCompetencia(e.target.value);
                                    setPage(1);
                                }}
                                startAdornment={<FilterListIcon sx={{ color: 'action.active', mr: 1 }} />}
                            >
                                <MenuItem value=""><em>Selecione a Competência</em></MenuItem>
                                {competencias.map(comp => (
                                    <MenuItem key={comp} value={comp}>{comp}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {selectedCompetencia && items.length > 0 && (
                        <Grid item xs={12} md={12}>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteSweepIcon />}
                                onClick={() => setDeleteDialog({ open: true, type: 'competencia', id: null, competencia: selectedCompetencia })}
                                size="small"
                            >
                                Excluir Todos os Insumos desta Competência
                            </Button>
                        </Grid>
                    )}
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            placeholder="Buscar código ou descrição..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : items.length === 0 ? (
                    <Typography variant="body1" align="center" color="text.secondary" sx={{ py: 4 }}>
                        {selectedCompetencia ? 'Nenhum item encontrado.' : 'Selecione uma competência para visualizar.'}
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Tabela de Insumos</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    onClick={handleDeleteAllSupplies}
                                >
                                    Excluir Todos
                                </Button>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<FaPlus />}
                                    onClick={handleOpenAdd}
                                >
                                    Adicionar Insumo
                                </Button>
                            </Box>
                        </Box>
                        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>TUSS</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Custo Médio</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item) => (
                                        <TableRow key={item.id} hover>
                                            <TableCell>{item.codigo_insumo}</TableCell>
                                            <TableCell>{item.codigo_tuss}</TableCell>
                                            <TableCell>{item.descricao}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                                R$ {parseFloat(item.custo_medio).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton size="small" color="primary" onClick={() => handleOpenEdit(item)} title="Editar insumo">
                                                    <FaEdit />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => setDeleteConfirmation(item.id)} title="Excluir insumo">
                                                    <FaTrash />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary">
                                Mostrando {items.length} de {total} itens
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                >
                                    Anterior
                                </Button>
                                <Typography variant="body2">
                                    Página {page}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={items.length < 50}
                                >
                                    Próxima
                                </Button>
                            </Stack>
                        </Box>
                    </>
                )}
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => !deleting && setDeleteDialog({ open: false, type: null, id: null, competencia: null })}>
                <DialogTitle>
                    {deleteDialog.type === 'item' ? 'Confirmar Exclusão' : 'Confirmar Exclusão em Massa'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteDialog.type === 'item'
                            ? 'Tem certeza que deseja excluir este insumo?'
                            : `Tem certeza que deseja excluir TODOS os insumos da competência ${deleteDialog.competencia}? Esta ação não pode ser desfeita.`
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, type: null, id: null, competencia: null })} disabled={deleting}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={deleteDialog.type === 'item' ? handleDeleteItem : handleDeleteCompetencia}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                    >
                        {deleting ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Add/Edit Modal */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle>{editingItem ? 'Editar Insumo' : 'Adicionar Insumo'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Código do Insumo"
                        fullWidth
                        value={formData.codigo_insumo}
                        onChange={(e) => setFormData({ ...formData, codigo_insumo: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Código TUSS"
                        fullWidth
                        value={formData.codigo_tuss}
                        onChange={(e) => setFormData({ ...formData, codigo_tuss: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Descrição"
                        fullWidth
                        multiline
                        rows={2}
                        value={formData.descricao}
                        onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    />
                    <TextField
                        margin="dense"
                        label="Custo Médio"
                        type="number"
                        fullWidth
                        value={formData.custo_medio}
                        onChange={(e) => setFormData({ ...formData, custo_medio: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={!!deleteConfirmation} onClose={() => setDeleteConfirmation(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir este insumo?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmation(null)}>Cancelar</Button>
                    <Button onClick={handleDeleteCRUD} color="error" variant="contained">Excluir</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SupplyTablesPage;
